/**
 * Win celebration overlay — a confetti burst + soft flash that every game
 * fires the moment a prize is decided, just before `onWin` hands off to the
 * PrizeReveal screen. Pure CSS transforms (keyframes in globals.css), no
 * dependency, respects reduced-motion via the stage's `data-game-motion`.
 *
 *   const celebration = useCelebration();
 *   // on win:  celebration.fire();
 *   <Celebration controller={celebration} primary={primary} accent={accent} />
 */
import { useCallback, useMemo, useState } from 'react';
import { tint, withAlpha } from './colors';

export interface CelebrationController {
  burst: number;
  fire: () => void;
}

export function useCelebration(): CelebrationController {
  const [burst, setBurst] = useState(0);
  const fire = useCallback(() => setBurst((b) => b + 1), []);
  return { burst, fire };
}

interface CelebrationProps {
  controller: CelebrationController;
  primary: string;
  accent: string;
  /** Confetti piece count. */
  pieces?: number;
}

export function Celebration({
  controller,
  primary,
  accent,
  pieces = 70,
}: CelebrationProps) {
  const { burst } = controller;
  const palette = useMemo(
    () => [accent, tint(accent, 0.35), tint(primary, 0.45), '#FFD54A', '#FFFFFF'],
    [accent, primary],
  );

  const confetti = useMemo(() => {
    if (burst === 0) return [];
    return Array.from({ length: pieces }, (_, i) => {
      const left = Math.random() * 100;
      const drift = (Math.random() - 0.5) * 160;
      const delay = Math.random() * 0.25;
      const duration = 1.6 + Math.random() * 1.4;
      const size = 6 + Math.random() * 7;
      const color = palette[i % palette.length];
      const rounded = Math.random() > 0.5;
      return { id: `${burst}-${i}`, left, drift, delay, duration, size, color, rounded };
    });
  }, [burst, pieces, palette]);

  if (burst === 0) return null;

  return (
    <div
      key={burst}
      className="pointer-events-none absolute inset-0 z-30 overflow-hidden rounded-[inherit]"
      aria-hidden
    >
      {/* central flash */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 45%, ${withAlpha(
            accent,
            0.5,
          )} 0%, transparent 60%)`,
          animation: 'game-flash 700ms ease-out both',
        }}
      />
      {confetti.map((c) => (
        <span
          key={c.id}
          className="absolute top-0 block"
          style={
            {
              left: `${c.left}%`,
              width: c.size,
              height: c.size * (c.rounded ? 1 : 1.6),
              background: c.color,
              borderRadius: c.rounded ? '9999px' : '2px',
              '--cx': `${c.drift}px`,
              animation: `game-confetti-fall ${c.duration}s cubic-bezier(.25,.6,.4,1) ${c.delay}s both`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
